import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { AdminConfigPaths } from 'src/store/adminConfigPageSlice/common/constants';
import {
  setCurrentPage,
  setTotalPages,
} from 'src/store/adminConfigPageSlice/regionTasksPageSlice/regionTasksPageSlice';
import { type RegionFormTableRow } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

// **** Types **** //

interface OnSortProps {
  sortColumn: keyof RegionFormTableRow;
  sortDirection: TableSortDirection;
}

// **** Components **** //

/**
 * Default Component
 */
const RestrictionFormTable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState<keyof RegionFormTableRow>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');

  // Extract table filter parameters
  const { filteredRegionTableData, searchField, currentPage, perPage } = useAppSelector(
    (state) => state.regionTasksConfigPage,
  );
  const [sortedTableData, setSortedTableData] = useState(filteredRegionTableData);

  // Listen for data/filter changes
  useEffect(() => {
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      filteredRegionTableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setSortedTableData(paginatedData as RegionFormTableRow[]);
    dispatch(setTotalPages(totalPages));
  }, [sortColumn, sortDirection, filteredRegionTableData, searchField, currentPage, perPage, dispatch]);

  // Go back to first page when filter/sort
  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [searchField, sortColumn, sortDirection]);

  // Table onSort callback handler
  const onSort = useCallback(({ sortColumn, sortDirection }: OnSortProps) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  }, []);

  // Click the "Edit" icon
  const clickEditRegion = (regionId: number) => {
    const newPath = AdminConfigPaths.Form.EDIT + '/' + regionId;
    navigate(newPath, { replace: true });
  };

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="view-and-edit-tasks__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell sortColumn="name">State/Region/Province</TableCell>
          <TableCell sortColumn="country">Applicable Country</TableCell>
          <TableCell sortColumn="formName">Form Name</TableCell>
          <TableCell sortColumn="isActive">Is Active?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>
        {sortedTableData.map((row) => (
          <UncontrolledExpandableRow key={row.id} data-testid="restriction-form-table__row">
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.country}</TableCell>
            <TableCell>{row.formName}</TableCell>
            <TableCell>{row.isActive ? 'Yes' : 'No'}</TableCell>
            <TableCell>{row.description}</TableCell>
            <TableCell>
              <Button
                type="icon"
                data-testid="view-and-edit-tasks__edit-icon"
                className="view-and-edit-tasks__edit-icon"
                onClick={() => clickEditRegion(row.id)}
              >
                <Icon tokens={editTokens}></Icon>
              </Button>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

// **** Export **** //

export default RestrictionFormTable;
